/**
 * VÉRAULT — DayWeatherChip (Jun 2026 · Portugal build, reusable)
 * Calm, location-exact live forecast line for itinerary days & spots.
 * Reads /api/weather-live (Open-Meteo, cached). Never invents data.
 */
import React from "react";
import { CloudSun, Wind } from "lucide-react";
import { api } from "../index";

const memCache = {};

export default function DayWeatherChip({ weather, date, showWind = false, compact = false, testid = "day-weather" }) {
  const [w, setW] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const key = weather ? `${weather.lat}:${weather.lng}:${date || ""}` : null;

  React.useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    if (memCache[key]) { setW(memCache[key]); return undefined; }
    const attempt = (retriesLeft) => {
      api.get(`/weather-live`, { params: { lat: weather.lat, lng: weather.lng, date, label: weather.label } })
        .then(({ data }) => {
          if (!alive) return;
          if (data?.available) { memCache[key] = data; setW(data); }
          else if (retriesLeft > 0) setTimeout(() => alive && attempt(retriesLeft - 1), 2000);
          else setFailed(true);
        })
        .catch(() => {
          if (!alive) return;
          if (retriesLeft > 0) setTimeout(() => alive && attempt(retriesLeft - 1), 2000);
          else setFailed(true);
        });
    };
    attempt(1);
    return () => { alive = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!weather) return null;
  if (failed) {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))]" data-testid={`${testid}-unavailable`}>
        <CloudSun className="size-3" strokeWidth={1.5} /> Weather data temporarily unavailable
      </span>
    );
  }
  if (!w) return null;

  const day = w.day;
  const cur = w.current || {};
  const updated = w.fetched_at
    ? new Date(w.fetched_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : null;
  const gUrl = `https://www.google.com/search?q=${encodeURIComponent("weather " + (w.label || weather.label || ""))}`;

  return (
    <a
      href={gUrl}
      target="_blank"
      rel="noreferrer noopener"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-2 flex-wrap rounded-full border border-[rgba(0,0,0,0.06)] bg-white/60 font-mono uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))] hover:border-[hsl(var(--accent-gold))]/50 hover:text-[hsl(var(--foreground))] transition-colors ${compact ? "px-2 py-0.5 text-[8.5px]" : "px-2.5 py-1 text-[9.5px]"}`}
      data-testid={testid}
      title={updated ? `Open-Meteo · updated ${updated} · Tap for live Google forecast` : "Tap for live Google forecast"}
    >
      <CloudSun className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
      {!compact && <span className="text-[hsl(var(--foreground))]">{w.label}</span>}
      {day ? (
        <>
          <span>{Math.round(day.temp_high)}° / {Math.round(day.temp_low)}°</span>
          {day.condition && <span>{day.condition}</span>}
          {!compact && day.rain_probability != null && day.rain_probability > 5 && <span>Rain {day.rain_probability}%</span>}
          {(showWind || (day.gusts_mph || 0) >= 25) && day.wind_mph != null && (
            <span className="inline-flex items-center gap-1">
              <Wind className="size-3" strokeWidth={1.5} />
              {Math.round(day.wind_mph)} mph{day.gusts_mph != null ? ` · gusts ${Math.round(day.gusts_mph)}` : ""}
            </span>
          )}
          {!compact && day.uv_index != null && day.uv_index >= 6 && <span>UV {Math.round(day.uv_index)}</span>}
        </>
      ) : cur.temp != null ? (
        <>
          <span>Now {Math.round(cur.temp)}°</span>
          {cur.condition && <span>{cur.condition}</span>}
          {(showWind || (cur.gusts_mph || 0) >= 25) && cur.wind_mph != null && (
            <span className="inline-flex items-center gap-1">
              <Wind className="size-3" strokeWidth={1.5} />
              {Math.round(cur.wind_mph)} mph{cur.gusts_mph != null ? ` · gusts ${Math.round(cur.gusts_mph)}` : ""}
            </span>
          )}
        </>
      ) : (
        <span>Forecast not yet available</span>
      )}
      {!compact && updated && <span className="opacity-60 normal-case">updated {updated}</span>}
    </a>
  );
}
